import * as React from "react";
import { useState } from "react";
import { formatDate } from "@/utils";
import { Box, Card, Fade, Grid, Link, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { ClockIcon } from "@phosphor-icons/react/dist/ssr/Clock";
import { PhoneIcon } from "@phosphor-icons/react/dist/ssr/Phone";
import { useParams } from "react-router-dom";

import { contactsKeys, useContact } from "@/hooks/api/use-contacts";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { CallLogs } from "@/components/call-logs";
import { useAnimation } from "@/components/core/animation-provider";
import { InfoCard } from "@/components/core/info-card/info-card";
import { RouterLink } from "@/components/core/link";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";
import { FilterType } from "@/components/customTable/types";
import { StatisticCard } from "@/components/statisticCard/statistic-card";

const animationDelay = 300;

export function ContactView(): React.JSX.Element {
	const { isAnimated } = useAnimation();

	const [isInfoCardVisible, setIsInfoCardVisible] = useState(isAnimated);

	const { contactId } = useParams();

	const { data: contactData, isLoading: isContactLoading } = useContact(contactId || "");

	const isLoading = isContactLoading;

	React.useEffect(() => {
		setTimeout(
			() => {
				setIsInfoCardVisible(true);
			},
			(animationDelay * (statisticCards.length + 1)) / 2
		);
	}, []);

	const statisticCards = React.useMemo(() => {
		return [
			{
				title: "Total Calls",
				value: 0,
				icon: <PhoneIcon color="var(--mui-palette-info-main)" width={20} height={20} />,
				additonalInfo: "Total calls made",
			},
			{
				title: "AI Minutes",
				value: 0,
				icon: <ClockIcon color="var(--mui-palette-warning-main)" width={20} height={20} />,
				additonalInfo: "Minutes of AI conversation",
			},
		];
	}, [contactData]);

	const campaignInfo = React.useMemo(() => {
		return {
			title: "Contact Information",
			subtitle: "Сontact details",
			fields: [
				{ label: "Phone", value: contactData?.phone || "N/A", type: "text" as const },
				{
					label: "Tags",
					value: contactData?.tags.map((tag: any) => tag.label).join(", ") || "N/A",
					type: "tags" as const,
				},
				{ label: "Created At", value: formatDate(contactData?.createdAt) || "N/A", type: "date" as const },
				{ label: "Updated At", value: formatDate(contactData?.updatedAt) || "N/A", type: "date" as const },
				...(contactData?.attributes
					? Object.entries(contactData.attributes).map(([key, value]) => ({
							label: key?.replace(/([a-z])([A-Z])/g, "$1 $2"),
							value: typeof value === "boolean" ? (value ? "Yes" : "No") : value?.toString(),
							type: "text" as const,
						}))
					: []),
			],
		};
	}, [contactData]);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/contacts"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Contacts
							</Link>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								{isLoading ? <CustomSkeleton width={100} height={20} /> : contactData?.phone}
							</Typography>
						</Breadcrumbs>

						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Box>
									<Typography variant="h4">Contact Details</Typography>
								</Box>
							</Box>
						</Stack>
					</Stack>

					{/* <Grid container spacing={3}>
						{statisticCards.map((card, index) => (
							<Grid
								key={index}
								size={{
									md: 3,
									sm: 6,
									xs: 12,
								}}
							>
								<StatisticCard
									{...card}
									isLoading={isLoading}
									animationDelay={index * animationDelay}
									timeout={animationDelay * statisticCards.length}
									isLast={index === statisticCards.length - 1}
								/>
							</Grid>
						))}
					</Grid> */}

					<Grid container spacing={3}>
						<Fade in={isInfoCardVisible} timeout={isAnimated ? 0 : animationDelay * statisticCards.length}>
							<Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
								<InfoCard
									title={campaignInfo.title}
									subtitle={campaignInfo.subtitle}
									fields={campaignInfo.fields}
									isLoading={isLoading}
								/>
							</Grid>
						</Fade>
					</Grid>

					<Card sx={{ position: "relative" }}>
						<CallLogs
							queryKey={contactsKeys.calls(contactId!)}
							endpoint={`/calls?contactId=${contactId}`}
							filtersOptions={[
								{
									name: "campaignId",
									label: "Campaign",
									value: "",
									type: FilterType.FETCH_SELECT,
									getOptionsUrl: "/campaigns",
									fetchLabel: "title",
									fetchValue: "id",
								},
							]}
						/>
					</Card>
				</Stack>
			</Box>
		</React.Fragment>
	);
}
